import { useMemo, useRef, useState, KeyboardEvent } from 'react'
import { HeaderContext } from '@tanstack/react-table'
import { format, isAfter, isToday, parse } from 'date-fns'
import clsx from 'clsx'

import { Button } from '@/ui/Button'
import { Popup, PopupRef } from '@/ui/Popup'
import { generateTimeOptions } from '../../helpers/generateTimeOptions'
import { SuggestionInputTable } from '@/ui/SuggestionInputTable'
import { DatePicker } from '@/ui/DatePicker'
import { Checkbox } from '@/ui/Checkbox'
import { getFormattedDate } from '@/helpers/time'
import { isValidTimeFormat } from '@/helpers/isValidTimeFormat'
import { useDateStore } from '@/components/DateInput'

import styles from './DateHeader.module.scss'
import commonStyles from './Common.module.scss'

import ArrowDownIcon from '../../assets/arrow-down.svg'

type Props<T> = HeaderContext<T, unknown> & {
    label: string
    customTimeOptionsGenerator?: (includePreviousDay: boolean, limitToTime?: Date) => { id: string; label: string; value: string }[]
    disableFutureDates?: boolean
}

const TODAY = new Date()

export const DateHeader = <T,>({ column, table, label, customTimeOptionsGenerator, disableFutureDates = true }: Props<T>) => {
    const { date } = useDateStore()
    const popupRef = useRef<PopupRef>(null)
    const [from, setFrom] = useState<Date>(TODAY)
    const [fromTime, setFromTime] = useState('')
    const [fromTimeAllDay, setFromTimeAllDay] = useState(false)
    const [to, setTo] = useState<Date>(TODAY)
    const [toTime, setToTime] = useState('')
    const [toTimeAllDay, setToTimeAllDay] = useState(false)

    const state = table.getState()

    const isActive = useMemo(
        () =>
            state.sorting.find((item) => item.id === column.id) ||
            state.columnFilters.find((item) => item.id === column.id),
        [state]
    )

    const timeOptionsGenerator = customTimeOptionsGenerator || generateTimeOptions
    const timeOptions = timeOptionsGenerator(isToday(new Date(date)))

    const isFromFilled = !!from && (fromTimeAllDay || isValidTimeFormat(fromTime))
    const isToFilled = !!to && (toTimeAllDay || isValidTimeFormat(toTime))

    const isButtonDisabled =
        (!!fromTime && !isValidTimeFormat(fromTime) && !fromTimeAllDay) ||
        (!!toTime && !isValidTimeFormat(toTime) && !toTimeAllDay) ||
        (isFromFilled && isToFilled && isAfter(from, to)) ||
        (isValidTimeFormat(fromTime) &&
            isValidTimeFormat(toTime) &&
            isFromFilled &&
            isToFilled &&
            isAfter(parse(fromTime, 'HH:mm', from), parse(toTime, 'HH:mm', to))) ||
        (!isFromFilled && !isToFilled)

    const applyFilters = () => {
        if (isButtonDisabled) {
            return
        }

        const filter: Record<string, string> = {}
        if (isFromFilled) {
            const fromTimeSelected = fromTimeAllDay ? '00:00' : fromTime
            const fromFilter = getFormattedDate({ time: fromTimeSelected, date: format(from, 'dd/MM/yyyy') })

            filter.from = fromFilter
        }

        if (isToFilled) {
            const toTimeSelected = toTimeAllDay ? '23:59' : toTime
            const toFilter = getFormattedDate({ time: toTimeSelected, date: format(to, 'dd/MM/yyyy') })
            filter.to = toFilter
        }

        column.setFilterValue(filter)
        popupRef.current?.closePopup()
    }

    const clearFrom = () => {
        setFrom(TODAY)
        setFromTime('')
        setFromTimeAllDay(false)
    }

    const clearTo = () => {
        setTo(TODAY)
        setToTime('')
        setToTimeAllDay(false)
    }

    const cancel = () => {
        popupRef.current?.closePopup()
        clearFrom()
        clearTo()
        column.setFilterValue(undefined)
    }
    const handleTimeKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', ':']
        // Allow numbers 0-9
        if (/^\d$/.test(e.key)) {
            return
        }
        if (allowedKeys.includes(e.key)) {
            return
        }
        e.preventDefault()
    }
    const handleFromTimeChange = (value: string) => {
        if (value.length === 2 && !value.includes(':')) {
            setFromTime(`${value}:`)
        } else {
            setFromTime(value)
        }
    }
    const handleToTimeChange = (value: string) => {
        if (value.length === 2 && !value.includes(':')) {
            setToTime(`${value}:`)
        } else {
            setToTime(value)
        }
    }

    return (
        <Popup
            ref={popupRef}
            triggerComponent={(open: boolean) => (
                <Button
                    size="small"
                    variant="transparent"
                    className={clsx(commonStyles.trigger, { [commonStyles.active]: open || isActive })}
                >
                    {label}
                    <ArrowDownIcon />
                </Button>
            )}
            attachToTrigger
            placement="bottom-end"
            className={styles.popup}
        >
            <div className={styles.dateContainer}>
                <div className={styles.dateWrapper}>
                    <div className={styles.titleWrapper}>
                        <h4 className={styles.title}>From</h4>
                        <button
                            className={clsx(styles.clear, {
                                [styles.clearActive]: from && (fromTime || fromTimeAllDay),
                            })}
                            onClick={clearFrom}
                        >
                            Clear
                        </button>
                    </div>
                    <DatePicker className={styles.datePicker} value={from} onChange={setFrom} disableFutureDates={disableFutureDates} />
                    <div className={styles.timeContainer}>
                        <SuggestionInputTable
                            placeholder={fromTimeAllDay ? 'All day' : 'Time'}
                            options={timeOptions}
                            value={fromTime}
                            className={styles.timePopup}
                            pattern="[0-9]{2}:[0-9]{2}"
                            maxLength={5}
                            disabled={fromTimeAllDay}
                            onKeyDown={handleTimeKeyDown}
                            onChange={handleFromTimeChange}
                        />
                        <div className={styles.allDayWrapper}>
                            <p className={styles.label}>All day</p>
                            <Checkbox
                                label="All day"
                                variant="withoutLabel"
                                onChange={(value) => {
                                    value && setFromTime('')
                                    setFromTimeAllDay(value)
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.dateWrapper}>
                    <div className={styles.titleWrapper}>
                        <h4 className={styles.title}>To</h4>
                        <button
                            className={clsx(styles.clear, {
                                [styles.clearActive]: to && (toTime || toTimeAllDay),
                            })}
                            onClick={clearTo}
                        >
                            Clear
                        </button>
                    </div>
                    <DatePicker className={styles.datePicker} value={to} onChange={setTo} disableFutureDates={disableFutureDates} />
                    <div className={styles.timeContainer}>
                        <SuggestionInputTable
                            placeholder={toTimeAllDay ? 'All day' : 'Time'}
                            options={timeOptions}
                            value={toTime}
                            className={styles.timePopup}
                            pattern="[0-9]{2}:[0-9]{2}"
                            maxLength={5}
                            disabled={toTimeAllDay}
                            onKeyDown={handleTimeKeyDown}
                            onChange={handleToTimeChange}
                        />
                        <div className={styles.allDayWrapper}>
                            <p className={styles.label}>All day</p>
                            <Checkbox
                                label="All day"
                                variant="withoutLabel"
                                onChange={(value) => {
                                    value && setToTime('')
                                    setToTimeAllDay(value)
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <Button rounded size="small" variant="tertiary" onClick={cancel}>
                    Cancel
                </Button>
                <Button rounded size="small" variant="secondary" disabled={isButtonDisabled} onClick={applyFilters}>
                    Done
                </Button>
            </div>
        </Popup>
    )
}
